import * as THREE from 'three';

/**
 * Custom getTransmissionSample.
 *
 * Mip-only blur (v5/v6) reads as a few giant soft “pixels” that swim while
 * orbiting — low mips are coarse and slide under refraction. Prefer a dense
 * high-res screen-space kernel with almost no mip depth: Transmit Blur controls
 * tap radius; roughness only adds a tiny lod bias.
 *
 * Keep transmissionResolutionScale ≤ 2 (higher can exceed max texture size → black glass).
 */
const TRANSMISSION_FN_MARKER = '/* c3d_transmit_blur_v7 */';

const TRANSMISSION_FN_ORIGINAL =
  'vec4 getTransmissionSample( const in vec2 fragCoord, const in float roughness, const in float ior ) {\n' +
  '\t\tfloat lod = log2( transmissionSamplerSize.x ) * applyIorToRoughness( roughness, ior );\n' +
  '\t\treturn textureBicubic( transmissionSamplerMap, fragCoord.xy, lod );\n' +
  '\t}';

const TRANSMISSION_FN_PATCHED =
  'vec4 getTransmissionSample( const in vec2 fragCoord, const in float roughness, const in float ior ) {\n' +
  `\t\t${TRANSMISSION_FN_MARKER}\n` +
  '\t\t#ifdef TRANSMIT_BLUR_VAL\n' +
  '\t\tfloat blur = float( TRANSMIT_BLUR_VAL );\n' +
  '\t\t#else\n' +
  '\t\tfloat blur = 0.0;\n' +
  '\t\t#endif\n' +
  '\t\tfloat roughF = applyIorToRoughness( roughness, ior );\n' +
  '\t\tfloat eff = max( blur, roughF );\n' +
  '\t\tif ( eff < 0.001 ) return textureBicubic( transmissionSamplerMap, fragCoord.xy, 0.0 );\n' +
  '\t\t// Keep LOD tiny — deep mips = chunky overblur that crawls when rotating.\n' +
  '\t\tfloat lod = min( log2( max( transmissionSamplerSize.x, 1.0 ) ) * roughF * 0.35, 1.15 );\n' +
  '\t\t// Transmit Blur → screen-space radius in texels (samples the full-res buffer).\n' +
  '\t\tfloat spread = blur * 12.0 + roughF * 5.0;\n' +
  '\t\tif ( spread < 0.4 ) return textureBicubic( transmissionSamplerMap, fragCoord.xy, lod );\n' +
  '\t\tvec2 texel = 1.0 / transmissionSamplerSize;\n' +
  '\t\tvec4 acc = vec4( 0.0 );\n' +
  '\t\tfloat tw = 0.0;\n' +
  '\t\tfor ( int x = -3; x <= 3; x++ ) {\n' +
  '\t\t\tfor ( int y = -3; y <= 3; y++ ) {\n' +
  '\t\t\t\tfloat fx = float( x );\n' +
  '\t\t\t\tfloat fy = float( y );\n' +
  '\t\t\t\tfloat d2 = fx * fx + fy * fy;\n' +
  '\t\t\t\tfloat w = exp( -d2 * 0.26 );\n' +
  '\t\t\t\tvec2 o = vec2( fx, fy ) * texel * spread;\n' +
  '\t\t\t\tacc += textureBicubic( transmissionSamplerMap, fragCoord.xy + o, lod ) * w;\n' +
  '\t\t\t\ttw += w;\n' +
  '\t\t\t}\n' +
  '\t\t}\n' +
  '\t\treturn acc / max( tw, 1e-4 );\n' +
  '\t}';

export function patchTransmissionBlurShader(): void {
  const chunk = THREE.ShaderChunk as Record<string, string>;
  const key = 'transmission_pars_fragment';
  if (!chunk[key]) return;
  if (chunk[key].includes(TRANSMISSION_FN_MARKER)) return;

  if (chunk[key].includes(TRANSMISSION_FN_ORIGINAL)) {
    chunk[key] = chunk[key].replace(TRANSMISSION_FN_ORIGINAL, TRANSMISSION_FN_PATCHED);
    return;
  }

  const fnStart = chunk[key].indexOf('vec4 getTransmissionSample(');
  if (fnStart < 0) return;
  let i = chunk[key].indexOf('{', fnStart);
  if (i < 0) return;
  let depth = 0;
  for (; i < chunk[key].length; i++) {
    const ch = chunk[key][i];
    if (ch === '{') depth++;
    else if (ch === '}') {
      depth--;
      if (depth === 0) {
        chunk[key] =
          chunk[key].slice(0, fnStart) + TRANSMISSION_FN_PATCHED + chunk[key].slice(i + 1);
        return;
      }
    }
  }
}

/** Sync TRANSMIT_BLUR_VAL define and force recompile when it changes. */
export function applyTransmitBlurDefine(
  mat: THREE.MeshPhysicalMaterial,
  blurVal: number,
): void {
  const defs = { ...(mat.defines || {}) } as Record<string, string>;
  const prev = defs.TRANSMIT_BLUR_VAL;
  if (blurVal > 0.001) {
    defs.TRANSMIT_BLUR_VAL = blurVal.toFixed(6);
  } else {
    delete defs.TRANSMIT_BLUR_VAL;
  }
  mat.defines = defs;
  if (prev !== defs.TRANSMIT_BLUR_VAL) {
    mat.needsUpdate = true;
  }
}

/**
 * Clamp transmission RT scale so width/height stay under the GPU max texture size.
 * Scale 4 at 1080p can exceed 4096 and silently kill the transmission pass (black glass).
 * Also keep pixel count modest — Three.js defaults the TX RT to 4× MSAA, which at
 * high DPR × scale 2 overflows `glRenderbufferStorageMultisample` and leaves
 * zero-size incomplete framebuffers (poisons the whole frame).
 */
export function clampTransmissionResolutionScale(
  requested: number,
  renderer: THREE.WebGLRenderer,
  viewWidth: number,
  viewHeight: number,
): number {
  const maxDim = Math.max(1, renderer.capabilities?.maxTextureSize ?? 4096);
  const w = Math.max(1, viewWidth);
  const h = Math.max(1, viewHeight);
  const maxByW = maxDim / w;
  const maxByH = maxDim / h;
  // Prefer ≤1.25 at large viewports so even if MSAA isn't patched yet, allocation survives.
  const px = w * h;
  const softCap = px > 4_000_000 ? 1 : px > 2_000_000 ? 1.25 : 2;
  const safe = Math.min(maxByW, maxByH, softCap) * 0.98;
  return Math.max(0.25, Math.min(requested, safe));
}

/**
 * Three.js creates the screen-space transmission RT with `samples: 4`.
 * At supersampled DPR that call fails → incomplete FBO cascade for the whole context.
 * Force samples to 0 before any allocate/setSize on transmission-like targets.
 */
let _txMsaaPatched = false;

export function patchTransmissionRenderTargetNoMsaa(): void {
  if (_txMsaaPatched || typeof window === 'undefined') return;
  _txMsaaPatched = true;

  const proto = THREE.WebGLRenderTarget.prototype as THREE.WebGLRenderTarget & {
    setSize: (width: number, height: number, depth?: number) => void;
  };
  const origSetSize = proto.setSize;

  proto.setSize = function patchedTxSetSize(this: THREE.WebGLRenderTarget, width: number, height: number, depth?: number) {
    const tex = this.texture as THREE.Texture | THREE.Texture[] | undefined;
    const single = tex && !Array.isArray(tex) ? tex : null;
    // Match Three.js transmission RT fingerprint (see WebGLRenderer transmission pass).
    if (
      single &&
      single.generateMipmaps &&
      single.minFilter === THREE.LinearMipmapLinearFilter &&
      (this.samples ?? 0) > 0
    ) {
      this.samples = 0;
    }
    return origSetSize.call(this, width, height, depth);
  };
}

patchTransmissionBlurShader();
patchTransmissionRenderTargetNoMsaa();
